<script setup lang="ts">
import type { PropType } from 'vue';

import type { AuctionItemEntityWithNegotiation } from '~/types/sale.type';

// PROPS
const { auction } = defineProps({
    auction: { type: Object as PropType<AuctionItemEntityWithNegotiation>, required: true },
});

// COMPOSABLES
const { tt, tn } = useTypedI18n();
const { negotiation, isBuyer, isUnderSellerReview, isEnded, isAccepted } = useAuctionNegotiation(() => auction);

// METHODS
const getAmountColor = (from: 'buyer' | 'seller'): string => {
    if (isEnded.value && !isAccepted.value) return 'text-default';

    const fromBuyer = from === 'buyer';

    if (isUnderSellerReview.value && fromBuyer) return 'text-error-600';

    return fromBuyer === isBuyer.value ? 'text-primary-500' : 'text-dimmed';
};
</script>

<template>
    <div class="grid grid-cols-2 gap-4">
        <div class="lg:space-y-1">
            <p class="text-dimmed text-xs lg:text-sm">
                {{ tt(isBuyer ? 'auctionNegotiation.sellerRequest' : 'auctionNegotiation.yourRequest') }}
            </p>
            <p class="text-2xl font-semibold lg:text-3xl" :class="getAmountColor('seller')">
                {{ tn(negotiation.currentSellerProposal.amount, 'currency') }}
            </p>
            <SaleFees
                v-if="!isEnded || isAccepted"
                wrapper-class="text-left"
                :reference-date="auction.startAt"
                :is-buyer="isBuyer"
                :base-amount="negotiation.currentSellerProposal.amount"
                :assets-count="auction.assets.length"
                :fee-configuration="auction.userMeta?.feeOverride"
            />
        </div>
        <div class="lg:space-y-1">
            <p class="text-dimmed text-xs lg:text-sm">
                {{ tt(isBuyer ? 'auctionNegotiation.yourOffer' : 'auctionNegotiation.buyerOffer') }}
            </p>
            <p class="text-2xl font-semibold lg:text-3xl" :class="getAmountColor('buyer')">
                {{ tn(negotiation.currentBuyerProposal.amount, 'currency') }}
            </p>
            <SaleFees
                v-if="!isEnded || isAccepted"
                wrapper-class="text-left"
                :reference-date="auction.startAt"
                :is-buyer="isBuyer"
                :base-amount="negotiation.currentBuyerProposal.amount"
                :assets-count="auction.assets.length"
                :fee-configuration="auction.userMeta?.feeOverride"
            />
        </div>
    </div>
</template>
